/**
 * Per-visitor consent mode (CDP consent hardening).
 *
 * Today the SDK mints a WebVisitor token and emits WIDGET_LOADED/PAGE_VIEWED
 * before the visitor has interacted at all — the only switch is the site-wide
 * C5 kill switch. `consentMode: 'pending'` makes identity OPT-IN per visitor:
 * until the host calls `floe.consent('granted')` the SDK writes no
 * `floe_anonymous_id`, mints no visitor token, enqueues no visitor/nudge
 * events, and opens no server-side conversation. The widget still renders and
 * a chat the visitor starts still works — as a session WITHOUT identity keys
 * (`visitorToken` / `anonymousId` are optional on every spawn path).
 *
 * Pure decisions only, so tests can run them in the `node` env; the SDK holds
 * the state and this module answers "what does that state mean?". The gating
 * call sites in onboarding-sdk.ts are pinned by source assertions
 * (tests/consent-mode.test.ts), same approach as host-page-telemetry.test.ts.
 */

/**
 * 'not-required' — today's behavior, and the default: identity flows without
 *                  any explicit grant.
 * 'pending'      — identity is deferred until `consent('granted')`.
 */
export type ConsentMode = 'not-required' | 'pending';

export type ConsentDecision = 'granted' | 'denied';

/**
 * Resolve the effective mode: explicit init option → per-site default from
 * validate-client-key (`websiteAgent.consentModeDefault`, additive — an older
 * floe-api serves nothing) → 'not-required'. Junk from either boundary reads
 * as absent, never as 'pending': failing toward today's behavior is what keeps
 * a malformed server payload from silently muting a site's analytics.
 */
export function resolveConsentMode(initOption: unknown, serverDefault: unknown): ConsentMode {
  if (initOption === 'pending' || initOption === 'not-required') return initOption;
  if (serverDefault === 'pending' || serverDefault === 'not-required') return serverDefault;
  return 'not-required';
}

/**
 * Fold a `consent()` call into the held decision.
 *
 * 'denied' is STICKY for the page load: once denied, a later 'granted' is
 * ignored — a host page that calls both has a bug, and the safe reading of
 * the conflict is the one that keeps data from leaving the browser. Junk
 * input changes nothing (customer snippets are plain JS).
 */
export function nextConsentDecision(
  current: ConsentDecision | null,
  requested: unknown,
): ConsentDecision | null {
  if (current === 'denied') return 'denied';
  if (requested === 'granted' || requested === 'denied') return requested;
  return current;
}

/**
 * May identity keys be written/attached and identity-bearing calls fired?
 * The single predicate every gated call site asks. Under 'not-required' no
 * grant is needed (today's behavior) — but an EXPLICIT 'denied' is honored in
 * every mode: a host that wired a reject button deserves it to mean something
 * even where consent wasn't required up front.
 */
export function identityAllowed(mode: ConsentMode, decision: ConsentDecision | null): boolean {
  if (decision === 'denied') return false;
  return mode === 'not-required' || decision === 'granted';
}

/**
 * May the SDK read page engagement off the host page (FLO-1096): the page
 * title, time on page, scroll depth, sections read, link and button clicks,
 * form submits, the referrer's host and campaign tags?
 *
 * Three things must all say yes: the SITE's visitor-analytics switch
 * (`eventCollectionEnabled`, from validate-client-key), the VISITOR's consent
 * ({@link identityAllowed}), and the agent being one that sits on a marketing
 * site (`runsOnMarketingSite` in agent-mode.ts). The first two are the gate
 * PAGE_VIEWED already has. The third keeps the capture out of a customer's
 * signed-in product, where titles, headings and buttons can name a person:
 * there a page view stays the path-only event it always was.
 *
 * The agent mode arrives as a boolean the caller derived from that one named,
 * positive capability. This module reads no mode flag itself, and a negated
 * per-mode flag is never the gate: it fails open for every mode added after
 * it. A yes here needs every input to be exactly what it should be.
 */
export const pageEngagementAllowed = (
  siteCollectionEnabled: boolean,
  onMarketingSite: boolean,
  mode: ConsentMode,
  decision: ConsentDecision | null,
): boolean =>
  siteCollectionEnabled === true &&
  onMarketingSite === true &&
  identityAllowed(mode, decision);
